import React, { useState, useEffect } from 'react';
import { useImageToolsOverlay } from '../context/ImageToolsOverlayContext';
import { getImageFiles } from '../api/images';
import StampTool from './image/Stamp';
import { RgbTool } from './image/Lupton';
import TrilogyTool from './image/Trilogy';
import OverlayTemplate from './common/OverlayTemplate';

const ImageToolsOverlay = () => {
  const {
    isOpen,
    isMinimized,
    closeOverlay,
    toggleMinimized,
    collectionId,
    prefilledData,
    activeTab,
    setActiveTab
  } = useImageToolsOverlay();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedTool, setExpandedTool] = useState(activeTab || 'stamp');

  // Load files when collection ID changes
  useEffect(() => {
    if (collectionId) {
      loadFiles();
    } else {
      setFiles([]);
    }
  }, [collectionId]);

  // Update expanded tool when activeTab changes
  useEffect(() => {
    if (activeTab) {
      setExpandedTool(activeTab);
    }
  }, [activeTab]);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getImageFiles(collectionId);
      setFiles(data);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load image files');
    } finally {
      setLoading(false);
    }
  };

  const handleToolToggle = (toolName) => {
    setExpandedTool(expandedTool === toolName ? null : toolName);
    setActiveTab(toolName);
  };

  // Icon component used in header and minimized tab
  const ImageIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );

  return (
    <OverlayTemplate
      isOpen={isOpen}
      isMinimized={isMinimized}
      onClose={closeOverlay}
      onToggleMinimize={toggleMinimized}
      title="Image Tools"
      subtitle={collectionId ? `Collection ID: ${collectionId}` : null}
      icon={ImageIcon}
      minimizedTabText="Images"
      width="640px"
      zIndex={54}
      minimizedTabPosition="35%"
      showBackdrop={false}
    >
      <div className="p-6">
        {!collectionId ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <svg className="w-16 h-16 text-ui-light-text-muted dark:text-ui-dark-text-muted mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            <p className="text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
              Please select an image collection to use these tools.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-4">
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Loading...</span>
              </div>
            )}

            {error && (
              <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
                {error}
              </div>
            )}

            {/* Stamp Tool Card */}
            <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden">
              <button
                className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
                onClick={() => handleToolToggle('stamp')}
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mr-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Stamp Tool</h2>
                    <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                      Create stamp images from a selected area
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary transform transition-transform ${expandedTool === 'stamp' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {expandedTool === 'stamp' && (
                <div className="px-6 pb-6">
                  <div className="h-1 w-16 bg-primary-200 dark:bg-primary-800 rounded mb-6"></div>
                  <StampTool
                    collectionId={collectionId}
                    availableFiles={files}
                    prefilledData={prefilledData}
                  />
                </div>
              )}
            </div>

            {/* RGB Tool Card */}
            <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden">
              <button
                className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
                onClick={() => handleToolToggle('rgb')}
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mr-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">RGB Tool</h2>
                    <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                      Create color composite images using multiple filters
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary transform transition-transform ${expandedTool === 'rgb' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {expandedTool === 'rgb' && (
                <div className="px-6 pb-6">
                  <div className="h-1 w-16 bg-primary-200 dark:bg-primary-800 rounded mb-6"></div>
                  <RgbTool availableFiles={files} collectionId={collectionId} prefilledData={prefilledData} />
                </div>
              )}
            </div>

            {/* Trilogy Tool Card */}
            <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden">
              <button
                className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
                onClick={() => handleToolToggle('trilogy')}
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mr-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Trilogy Tool</h2>
                    <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                      Advanced visualization with enhanced processing options
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary transform transition-transform ${expandedTool === 'trilogy' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {expandedTool === 'trilogy' && (
                <div className="px-6 pb-6">
                  <div className="h-1 w-16 bg-primary-200 dark:bg-primary-800 rounded mb-6"></div>
                  <TrilogyTool collectionId={collectionId} availableFiles={files} prefilledData={prefilledData} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </OverlayTemplate>
  );
};

export default ImageToolsOverlay;
